import { c as _c } from "react/compiler-runtime";
import { animated, useSpring } from '@react-spring/web';
import React, { FunctionComponent, PropsWithChildren, useState } from 'react';
import { useElementSize } from '../../hooks/useElementSize/useElementSize';
import { useSpringReset } from '../../hooks/useSpringReset';
import { prepareProps } from '../../utils/propUtils';
import styles from './ExpandableContainer.module.css';
export type ExpandableContainerProps = PropsWithChildren<{
  expanded: boolean;
  'data-testid'?: string;
}>;
export const ExpandableContainer: FunctionComponent<ExpandableContainerProps> = React.memo(props => {
  const $ = _c(21);
  let t0;
  if ($[0] !== props) {
    t0 = prepareProps({
      props
    });
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const {
    expanded,
    children,
    "data-testid": dataTestId
  } = t0;
  const [renderChildren, setRenderChildren] = useState(expanded);
  const [contentElement, setContentElement] = useState(null);
  const contentElementSize = useElementSize(contentElement);
  const reset = useSpringReset();
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      height: 0
    };
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const t2 = contentElementSize?.clientHeight || 100;
  let t3;
  if ($[3] !== t2) {
    t3 = {
      height: t2
    };
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      frequency: 0.5,
      damping: 0.5,
      mass: 0.5,
      clamp: true
    };
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const t5 = !expanded;
  let t6;
  let t7;
  if ($[6] !== expanded) {
    t6 = () => {
      if (expanded) {
        setRenderChildren(true);
      }
    };
    t7 = () => {
      if (!expanded) {
        setRenderChildren(false);
      }
    };
    $[6] = expanded;
    $[7] = t6;
    $[8] = t7;
  } else {
    t6 = $[7];
    t7 = $[8];
  }
  let t8;
  if ($[9] !== reset || $[10] !== t3 || $[11] !== t5 || $[12] !== t6 || $[13] !== t7) {
    t8 = {
      from: t1,
      to: t3,
      reset,
      config: t4,
      reverse: t5,
      onStart: t6,
      onRest: t7
    };
    $[9] = reset;
    $[10] = t3;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const contentWrapperStyle = useSpring(t8);
  const t9 = renderChildren ? children : null;
  let t10;
  if ($[15] !== t9) {
    t10 = <div ref={setContentElement} className={styles.contentContainer}>{t9}</div>;
    $[15] = t9;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== contentWrapperStyle || $[18] !== dataTestId || $[19] !== t10) {
    t11 = <animated.div className={styles.animationContainer} style={contentWrapperStyle} data-testid={dataTestId}>{t10}</animated.div>;
    $[17] = contentWrapperStyle;
    $[18] = dataTestId;
    $[19] = t10;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  return t11;
});